import { getErrorMessage } from '../utils/appErrorMessages';
import { getValidToken } from '../utils/tokenUtils';

const BASE_URL = '/api/e2e';

const buildHeaders = (token, includeJson = false) => ({
  ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

const parseResponse = async (response) => {
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return text || null;
};

/**
 * Parses a failed response and throws an enriched Error with:
 *   err.status    — HTTP status code
 *   err.detail    — raw backend message (English, for field-level mapping)
 *   err.errorCode — E2E-XXXX / MIMIR-XXXX code (if backend returned one)
 *   err.origin    — "CLIENT" | "SERVER"
 */
const throwFromResponse = async (response, method, url) => {
  const text = await response.text().catch(() => null);
  let detail    = null;
  let errorCode = null;
  let origin    = null;

  if (text) {
    try {
      const parsed = JSON.parse(text);
      detail    = parsed.message || parsed.error || text;
      errorCode = parsed.errorCode ?? null;
      origin    = parsed.origin ?? null;
    } catch {
      detail = text;
    }
  }

  // 1. AppErrorCode lookup (most specific)
  let message = getErrorMessage(errorCode, origin);

  // 2. HTTP status fallback
  if (!message) {
    switch (response.status) {
      case 401:
        message = 'Din session har gått ut. Logga in igen.';
        break;
      case 403:
        message = 'Du saknar behörighet för denna åtgärd.';
        break;
      case 404:
        message = detail || 'Resursen kunde inte hittas.';
        break;
      case 409:
        message = detail || 'Resursen befinner sig i ett ogiltigt tillstånd.';
        break;
      case 429:
        message = 'För många förfrågningar. Vänta en stund och försök igen.';
        break;
      case 400:
        message = detail || 'Ogiltig begäran.';
        break;
      default:
        message = `Något gick fel (HTTP ${response.status}). Försök igen.`;
    }
  }

  const err = new Error(message);
  err.status    = response.status;
  err.detail    = detail;
  err.errorCode = errorCode;
  err.origin    = origin;
  throw err;
};

const request = async (url, { method = 'GET', body } = {}) => {
  // Fetched fresh on every call (and refreshed if it's about to expire) so that
  // callers never need to re-fetch data just because AuthContext's token state
  // rotated - see docs/SESSION_AND_TOKEN_HANDLING.md.
  const token = await getValidToken();
  const response = await fetch(url, {
    method,
    headers: buildHeaders(token, body !== undefined),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    await throwFromResponse(response, method, url);
  }

  return parseResponse(response);
};

/**
 * Builds the correct base path depending on the user's role.
 * @param {boolean} isReviewer
 * @returns {string}
 */
const getBasePath = (isReviewer) =>
  isReviewer
    ? `${BASE_URL}/reviewer/submissions`
    : `${BASE_URL}/tester/submissions`;

export const getUserForms = async () =>
  request(`${BASE_URL}/tester/forms`);

export const getUserForm = async (formId) =>
  request(`${BASE_URL}/tester/forms/${formId}`);

export const createUserSubmission = async (payload) =>
  request(`${BASE_URL}/tester/submissions`, {
    method: 'POST',
    body: payload,
  });

export const updateUserSubmission = async (submissionId, payload) =>
  request(`${BASE_URL}/tester/submissions/${submissionId}`, {
    method: 'PUT',
    body: payload,
  });

/** Revise answers on commented questions while the submission is in clarification. */
export const updateClarificationAnswers = async (submissionId, payload) =>
  request(`${BASE_URL}/tester/submissions/${submissionId}/clarify-answers`, {
    method: 'PUT',
    body: payload,
  });

export const submitUserSubmission = async (submissionId) =>
  request(`${BASE_URL}/tester/submissions/${submissionId}/submit`, {
    method: 'POST',
  });

export const submitClarifiedSubmission = async (submissionId) =>
  request(`${BASE_URL}/tester/submissions/${submissionId}/clarified`, {
    method: 'POST',
  });

export const getMySubmissions = async () =>
  request(`${BASE_URL}/tester/submissions/mysubmissions`);

export const getUserSubmission = async (submissionId) =>
  request(`${BASE_URL}/tester/submissions/${submissionId}`);

export const getReviewerSubmissions = async () =>
  request(`${BASE_URL}/reviewer/submissions`);

export const getReviewerSubmission = async (submissionId) =>
  request(`${BASE_URL}/reviewer/submissions/${submissionId}`);

/** Archive search over finished cases, filtered by organisation and/or user. */
export const searchArchivedReviewerSubmissions = async (
  { organisation = '', user = '' } = {}
) => {
  const params = new URLSearchParams();
  if (organisation.trim()) params.set('organisation', organisation.trim());
  if (user.trim()) params.set('user', user.trim());

  const query = params.toString();
  return request(
    `${BASE_URL}/reviewer/submissions/archive${query ? `?${query}` : ''}`
  );
};

/** Organisations present in the reviewer's archive, for the search dropdown. */
export const getArchiveOrganisations = async () =>
  request(`${BASE_URL}/reviewer/submissions/archive/organisations`);

export const getE2EUserProfileById = async (userId) =>
  request(`${BASE_URL}/users/${encodeURIComponent(userId)}`);

export const assignReviewerSubmissionToMe = async (submissionId) =>
  request(`${BASE_URL}/reviewer/submissions/${submissionId}/assign-to-me`, {
    method: 'PUT',
  });

export const unassignReviewerSubmission = async (submissionId) =>
  request(`${BASE_URL}/reviewer/submissions/${submissionId}/unassign`, {
    method: 'PUT',
  });

/** Reviewers sharing a department with the caller; empty if the caller has none. */
export const getAssignableReviewers = async (submissionId) => {
  const data = await request(
    `${BASE_URL}/reviewer/submissions/${submissionId}/assignable-reviewers`
  );
  return Array.isArray(data) ? data : [];
};

export const assignReviewerSubmissionTo = async (submissionId, reviewerId) =>
  request(`${BASE_URL}/reviewer/submissions/${submissionId}/assign`, {
    method: 'PUT',
    body: { reviewerId },
  });

export const unassignReviewerFromSubmission = async (submissionId, reviewerId) =>
  request(`${BASE_URL}/reviewer/submissions/${submissionId}/unassign-reviewer`, {
    method: 'PUT',
    body: { reviewerId },
  });

export const approveReviewerSubmission = async (submissionId, comment) =>
  request(`${BASE_URL}/reviewer/submissions/${submissionId}/approve`, {
    method: 'POST',
    body: { comment },
  });

export const rejectReviewerSubmission = async (submissionId, comment) =>
  request(`${BASE_URL}/reviewer/submissions/${submissionId}/reject`, {
    method: 'POST',
    body: { comment },
  });

/**
 * Fetch all comments for a submission.
 */
export const getSubmissionComments = async (submissionId, isReviewer) => {
  return request(`${getBasePath(isReviewer)}/${submissionId}/comments`);
};

/**
 * Post a new comment on a submission.
 */
export const createSubmissionComment = async (
  submissionId,
  answerId,
  commentText,
  author,
  isReviewer
) => {
  return request(`${getBasePath(isReviewer)}/${submissionId}/comments`, {
    method: 'POST',
    body: {
      submissionId,
      answerId,
      commentText,
      author,
    },
  });
};

export const deleteSubmissionComment = async (
  submissionId,
  commentId,
  isReviewer
) => {
  return request(`${getBasePath(isReviewer)}/${submissionId}/comments/${commentId}`, {
    method: 'DELETE',
  });
};

export const requestClarification = async (submissionId, answerId, comment, isReviewer) => {
  const basePath = getBasePath(!!isReviewer);
  return request(`${basePath}/${submissionId}/clarify`, {
    method: 'POST',
    body: {
      answerId,
      comment,
    },
  });
};

// ========================
// ATTACHMENT ENDPOINTS
// ========================

export const uploadAttachment = async (submissionId, file) => {
  const token = await getValidToken();
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(
    `${BASE_URL}/tester/submissions/${submissionId}/attachments`,
    {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    }
  );

  if (!response.ok) {
    await throwFromResponse(response, 'POST', `${BASE_URL}/tester/submissions/${submissionId}/attachments`);
  }

  return response.json();
};

export const getAttachments = async (submissionId, isReviewer) => {
  const basePath = getBasePath(!!isReviewer);
  return request(`${basePath}/${submissionId}/attachments`);
};

export const downloadAttachment = async (submissionId, attachmentId, isReviewer) => {
  const token = await getValidToken();
  const basePath = getBasePath(!!isReviewer);
  const response = await fetch(
    `${basePath}/${submissionId}/attachments/${attachmentId}/download`,
    {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );

  if (!response.ok) {
    await throwFromResponse(response, 'GET', `${basePath}/${submissionId}/attachments/${attachmentId}/download`);
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition');
  let fileName = 'attachment.zip';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?(.+?)"?$/);
    if (match) fileName = match[1];
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const deleteAttachment = async (submissionId, attachmentId) =>
  request(`${BASE_URL}/tester/submissions/${submissionId}/attachments/${attachmentId}`, {
    method: 'DELETE',
  });
